import { graphql, navigate } from 'gatsby';
import React from 'react';

interface CaseStudiesProps {
  _template: string;
  css_class_name: string;
  case_study_grids: CaseStudyGrid[];
}

const CaseStudies: React.FC<CaseStudiesProps> = ({
  _template,
  css_class_name,
  case_study_grids,
}) => {
  return (
    <section className={`block ${_template} ${css_class_name}`}>
      <div className='container'>
        <div className='tiles-wrapper'>
          {case_study_grids.map((grid, index) => (
            <CaseStudyGrid
              key={index}
              grid={grid}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const CaseStudyGrid: React.FC<{ grid: CaseStudyGrid }> = ({ grid }) => {
  return (
    <div className={`tiles tiles--${grid.type}`}>
      {grid.case_studies.map((item, index) => (
        <CaseStudyTile
          key={index}
          item={item}
        />
      ))}
    </div>
  );
};

export const CaseStudyTile: React.FC<{
  item: CaseStudyTile;
  theme?: string;
  navigationTrigger?: string;
}> = ({ item, theme, navigationTrigger = 'card' }) => {
  let tileBackground = undefined;
  if (item.background && item.background.from) {
    const { from, to, degrees: itemDegrees } = item.background;
    let degrees = itemDegrees ? itemDegrees : 180;
    tileBackground = `linear-gradient(${degrees}deg, ${from} 0%, ${
      to ? to : from
    } 100%)`;
  }

  const { cover } = item;
  let coverPosition = '';
  let coverTransform = undefined;

  const tileTheme = theme ? `tile--${theme}` : 'tile--default';

  if (cover) {
    if (cover.horizontal_position) {
      coverPosition = `tile__cover--${cover.horizontal_position}`;
    }
    if (cover.offset) {
      const { horizontal, vertical } = cover.offset;
      coverTransform = `translate(${horizontal ?? 0}rem, ${vertical ?? 0}rem)`;
    }
  }

  const titleShort = item.title_short ? item.title_short : item.title;

  const clickHandler = (triggeredBy: string) => () => {
    if (triggeredBy === navigationTrigger && item.cta_url) {
      if (item.open_in_new_tab) {
        window.open(item.cta_url, '_blank');
      } else {
        navigate(item.cta_url);
      }
    }
  };

  const tileStyle = tileBackground
    ? ({ '--tile-background': 'red' } as React.CSSProperties)
    : {};

  // types => 2 rows, 4 cols, 1 row 2 cols, 1 cover
  return (
    <div
      className={`tile ${tileTheme} scale-on-hover shadow-on-hover`}
      style={{
        ...tileStyle,
        cursor: navigationTrigger === 'card' ? 'pointer' : 'default',
      }}
      onClick={clickHandler('card')}
    >
      <div className='logo logo--tile'>
        <img
          src={item.logo.icon}
          alt={item.logo.title}
        />
      </div>
      <p
        className='tile__title tile__title--long'
        dangerouslySetInnerHTML={{ __html: item.title }}
      />
      <p
        className='tile__title tile__title--short'
        dangerouslySetInnerHTML={{ __html: titleShort }}
      />
      {item.cta_text && (
        <div
          className={`btn btn--primary tile__cta`}
          onClick={clickHandler('cta')}
        >
          {item.cta_text}
          <span className='with-arrow'></span>
        </div>
      )}
      {cover && (
        <div className={`tile__cover ${coverPosition}`}>
          <img
            style={{ transform: coverTransform }}
            src={cover.image}
            alt={cover.image_alt_text}
          />
        </div>
      )}
    </div>
  );
};

export const caseStudyFragment = graphql`
  fragment CaseStudyTileFragment on CaseStudy {
    title
    title_short
    cta_text
    cta_url
    open_in_new_tab
    logo {
      title
      icon
    }
    cover {
      image
      image_alt_text
      horizontal_position
      offset {
        horizontal
        vertical
      }
    }
    background {
      degrees
      from
      to
    }
  }
`;

export default CaseStudies;
